/**
 * inferMissingSymbol.js
 *
 * WHY THIS EXISTS:
 * compile_result.json only provides message + line + column for "cannot find symbol" errors.
 * Maven's "symbol: class Foo / location:" detail lines are NOT captured in our pipeline.
 * Therefore we infer the missing identifier directly from the source line text + column position.
 * This is deterministic — no AI, no guessing, pure string parsing.
 *
 * source_lines come from compile_failure_snippets.json (files[filename].source_lines map),
 * which is fetched by gateReportService and threaded through the normalizer as issue.source_lines.
 */

/**
 * inferSymbolAtPosition(sourceLine, column)
 * column is 1-based (as stored in compile_result.json / errors_preview).
 * Returns { token, startIdx, endIdx } or { token: null } if nothing found.
 */
export function inferSymbolAtPosition(sourceLine, column) {
  if (!sourceLine || column == null) return { token: null }

  const line = sourceLine
  const idx  = column - 1   // convert to 0-based
  const isIdChar = (c) => /[A-Za-z0-9_$]/.test(c)

  let start = idx
  let end   = idx

  // If we're on an identifier character, expand outward
  if (idx < line.length && isIdChar(line[idx])) {
    while (start > 0 && isIdChar(line[start - 1])) start--
    while (end < line.length - 1 && isIdChar(line[end + 1])) end++
    const token = line.slice(start, end + 1)
    return { token, startIdx: start, endIdx: end }
  }

  // Not on identifier — walk left to find nearest
  let leftEnd = idx - 1
  while (leftEnd >= 0 && !isIdChar(line[leftEnd])) leftEnd--
  if (leftEnd >= 0) {
    let leftStart = leftEnd
    while (leftStart > 0 && isIdChar(line[leftStart - 1])) leftStart--
    const token = line.slice(leftStart, leftEnd + 1)
    return { token, startIdx: leftStart, endIdx: leftEnd }
  }

  // Walk right
  let rightStart = idx + 1
  while (rightStart < line.length && !isIdChar(line[rightStart])) rightStart++
  if (rightStart < line.length) {
    let rightEnd = rightStart
    while (rightEnd < line.length - 1 && isIdChar(line[rightEnd + 1])) rightEnd++
    const token = line.slice(rightStart, rightEnd + 1)
    return { token, startIdx: rightStart, endIdx: rightEnd }
  }

  return { token: null }
}

/**
 * inferSymbolKind(sourceLine, startIdx, endIdx, token)
 * Returns one of: 'class' | 'method' | 'field' | 'array' | 'unknown'
 *
 * Rules:
 * - token followed by '[' => 'array'
 * - token followed by '(' => 'method'
 * - token preceded by 'new ' or starts uppercase AND followed by ' ' + lowercase word (type position) => 'class'
 * - token starts uppercase and followed by '.' => 'class' (static ref)
 * - else => 'field'
 */
export function inferSymbolKind(sourceLine, startIdx, endIdx, token) {
  if (!sourceLine || !token) return 'unknown'

  const after          = sourceLine.slice(endIdx + 1).trimStart()
  const before         = sourceLine.slice(0, startIdx)
  const beforeTrimmed  = before.trimEnd()

  // Array: token followed by '['
  if (after.startsWith('[')) return 'array'

  // Method: token followed by '('
  if (after.startsWith('(')) return 'method'

  // Class: preceded by 'new'
  if (beforeTrimmed.endsWith('new')) return 'class'

  // Class: token starts uppercase and followed by '.' (static/type ref)
  if (/^[A-Z]/.test(token) && after.startsWith('.')) return 'class'

  // Class: token starts uppercase and is in type position (e.g. "BigDecimal foo" or cast "(BigDecimal)")
  if (/^[A-Z]/.test(token) && /^(\s+[a-z$_]|\s*\))/.test(after)) return 'class'

  // Default for uppercase starting token
  if (/^[A-Z]/.test(token)) return 'class'

  // Default: field / variable
  return 'field'
}

/**
 * inferMissingSymbol(issue)
 * Convenience wrapper: given an issue object with { line, column, source_lines }
 * returns { token, kind, sourceLine, line, column } or null.
 *
 * source_lines is a map of { "lineNumber": "source text" } where keys are 1-based
 * line numbers stored as strings (matching the compile_failure_snippets.json format).
 */
export function inferMissingSymbol(issue) {
  if (!issue) return null

  const line        = issue.line        ?? issue.lineNumber        ?? issue.javaLocation?.line ?? null
  const column      = issue.column      ?? issue.columnNumber      ?? null
  const sourceLines = issue.source_lines ?? issue.sourceLines      ?? {}

  if (!line || !column) return null

  const sourceLine = sourceLines[line] ?? sourceLines[String(line)] ?? null
  if (!sourceLine) return null

  const { token, startIdx, endIdx } = inferSymbolAtPosition(sourceLine, column)
  if (!token) return null

  const kind = inferSymbolKind(sourceLine, startIdx, endIdx, token)

  return { token, kind, sourceLine, line, column }
}
